/**
 * Supporter utilities - csillag rendszer kezelése
 */

export interface ServerDonation {
  id: number;
  user_id: number;
  user_name: string;
  amount: number;
  package_type: string;
  start_date: string;
  notes?: string;
  created_at: string;
}

export interface AdFreeTier {
  id: number;
  title: string;
  price: string;
  duration: string;
  features: string[];
  is_popular: boolean;
  link: string;
}

/**
 * Lekérdezi az aktív supporter listáját
 * @returns Promise<ServerDonation[]>
 */
export const fetchActiveSupporters = async (): Promise<ServerDonation[]> => {
  try {
    const response = await fetch('/api/supporters/active');
    if (!response.ok) {
      throw new Error('A támogatók betöltése sikertelen.');
    }
    return await response.json();
  } catch (error) {
    console.error('Hiba a supporter lekérdezéskor:', error);
    return [];
  }
};

/**
 * Ellenőrzi, hogy egy támogatás az adott évben aktív-e
 * Az év egész talamán érvényes, ha az év során történt a támogatás
 * @param donation - A támogatás objektuma
 * @param year - Az ellenőrizendő év (opcionális, alapértelmezésben az aktuális év)
 * @returns boolean - igaz, ha aktív az adott évben
 */
export const isActiveSupportment = (donation: ServerDonation, year?: number): boolean => {
  const startDate = new Date(donation.start_date);
  const today = new Date();
  const checkYear = year || today.getFullYear();
  
  // Az adat nem lehet a jövőben
  if (startDate > today) {
    return false;
  }
  
  // Az év egész talamán aktív, ha ebben az évben vagy előtte történt a támogatás
  const donationYear = startDate.getFullYear();
  return donationYear <= checkYear;
};

/**
 * Meghatározza, hogy hány csillag jár egy supporter-nek
 * Az összeg alapján: 1 csillag = alacsony, 2 csillag = közepes, 3 csillag = magas
 * @param amount - Támogatás összege
 * @returns number (1-3) - Csillagok száma
 */
export const getStarRatingByAmount = (amount: number): number => {
  if (amount >= 50000) return 3; // 50k+ = 3 csillag
  if (amount >= 20000) return 2; // 20k+ = 2 csillag
  return 1; // 1k+ = 1 csillag
};

/**
 * Meghatározza, hogy hány csillag jár egy supporter-nek
 * A csomag típusa alapján (prioritás alapján)
 * @param packageType - Csomag típusa (ID-ként érkezik stringként)
 * @param tiers - Az összes rendelkezésre álló csomag
 * @returns number (1-3) - Csillagok száma
 */
export const getStarRatingByTier = (packageType: string, tiers: AdFreeTier[]): number => {
  const tier = tiers.find(t => String(t.id) === packageType);
  if (!tier) return 1;
  
  // A tier sorrend alapján (első = legmagasabb, utolsó = legalacsonyabb)
  const tierIndex = tiers.findIndex(t => t.id === tier.id);
  if (tierIndex === 0) return 3;
  if (tierIndex === 1) return 2;
  return 1;
};

/**
 * Lekérdezi az AdFree tiereket
 * @returns Promise<AdFreeTier[]>
 */
export const fetchAdFreeTiers = async (): Promise<AdFreeTier[]> => {
  try {
    const response = await fetch('/api/supporters/tiers');
    if (!response.ok) {
      throw new Error('A csomagok betöltése sikertelen.');
    }
    return await response.json();
  } catch (error) {
    console.error('Hiba a csomag lekérdezéskor:', error);
    return [];
  }
};

/**
 * Meghatározza, hogy egy felhasználó supporter-e és hány csillagra jogosult
 * Az adott naptári évben aktív supporter-ek kapnak csillagokat
 * @param userId - Felhasználó ID-ja
 * @param supporterList - Az aktív supporter lista
 * @param tiers - Az összes rendelkezésre álló csomag
 * @param year - Az ellenőrizendő év (opcionális, alapértelmezésben az aktuális év)
 * @returns number (0-3) - Csillagok száma (0 = nem supporter)
 */
export const getUserStarRating = (
  userId: number,
  supporterList: ServerDonation[],
  tiers: AdFreeTier[],
  year?: number
): number => {
  const donation = supporterList.find(d => d.user_id === userId);
  if (!donation) return 0;
  
  // Ellenőrizzük, hogy az adott évben aktív-e a supporter
  if (!isActiveSupportment(donation, year)) {
    return 0;
  }
  
  // Kombinált logika: csomaghoz mérten
  return getStarRatingByTier(donation.package_type, tiers);
};

/**
 * Meghatározza a supporter szint nevét a csillagok száma alapján
 * @param starRating - Csillagok száma (1-3)
 * @returns string - A szint neve (Bronz, Ezüst, Arany)
 */
export const getSupporterLevelName = (starRating: number): string => {
  switch (starRating) {
    case 3:
      return 'Arany';
    case 2:
      return 'Ezüst';
    case 1:
      return 'Bronz';
    default:
      return 'Ismeretlen';
  }
};

/**
 * Meghatározza a supporter szint color-ét a csillagok száma alapján
 * @param starRating - Csillagok száma (1-3)
 * @returns string - A color osztály neve
 */
export const getSupporterLevelColor = (starRating: number): string => {
  switch (starRating) {
    case 3:
      return 'bg-yellow-600'; // Arany
    case 2:
      return 'bg-slate-300'; // Ezüst
    case 1:
      return 'bg-amber-700'; // Bronz
    default:
      return 'bg-yellow-600';
  }
};

